import { useQuery } from "@tanstack/react-query";
import { supabase } from "../services/supabase";

// Function to fetch posts filtered by categories from Supabase
const fetchPostsByCategory = async (categoryNames) => {
  try {
    // Normalize input to array and lowercase
    const normalizedCategories = !categoryNames
      ? []
      : Array.isArray(categoryNames)
        ? categoryNames.map((cat) => cat.toLowerCase())
        : [categoryNames.toLowerCase()];

    // If no categories provided, return empty array
    if (normalizedCategories.length === 0) {
      return [];
    }

    // Build the query with category filtering
    let query = supabase
      .from("posts")
      .select("*")
      .order("created_at", { ascending: false });

    // Try different filtering approaches based on how categories are stored
    // Method 1: If categories is stored as an array
    try {
      // Build OR conditions for multiple categories
      // Use the 'cs' (contains) operator for each category individually
      const orConditions = normalizedCategories.map(
        (cat) => `categories.cs.{${cat}}`
      );

      query = query.or(orConditions.join(","));
      const { data, error } = await query;

      if (!error && data && data.length > 0) {
        return data;
      }
    } catch (e) {
      console.log("Array contains method failed, trying alternative...");
    }

    // Method 2: If categories is stored as a string or JSON
    try {
      query = supabase
        .from("posts")
        .select("*")
        .order("created_at", { ascending: false });

      // Use OR conditions for multiple categories
      const orConditions = normalizedCategories.map(
        (cat) => `categories.ilike.%${cat}%`
      );

      query = query.or(orConditions.join(","));
      const { data, error } = await query;

      if (!error && data && data.length > 0) {
        return data;
      }
    } catch (e) {
      console.log("OR conditions method failed, trying final method...");
    }

    // Method 3: Fallback to simple text search
    try {
      query = supabase
        .from("posts")
        .select("*")
        .order("created_at", { ascending: false });

      // Use text search on categories field
      const searchTerm = normalizedCategories.join(" ");
      query = query.textSearch("categories", searchTerm);
      const { data, error } = await query;

      if (!error && data && data.length > 0) {
        return data;
      }
    } catch (e) {
      console.log("Text search method failed");
    }

    // If all methods fail, return empty array
    return [];
  } catch (error) {
    console.error("Error fetching posts by category:", error);
    return [];
  }
};

// Custom Hook to use TanStack Query for fetching posts by category
export function usePostsByCategory(categoryNames) {
  const query = useQuery({
    queryKey: ["posts", "category", categoryNames],
    queryFn: () => fetchPostsByCategory(categoryNames),
    enabled: !!categoryNames, // Only run query if categoryNames is provided
    staleTime: 1000 * 60 * 5, // 5 minutes
    cacheTime: 1000 * 60 * 10, // 10 minutes
    retry: 1, // Only retry once
    retryDelay: 1000, // Wait 1 second before retry
  });

  // Return both the full query object and a convenience data property
  return {
    ...query,
    data: query.data || [],
    posts: query.data || [], // Alias for backward compatibility
  };
}
